import { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';

import TireForm from '../components/TireForm';
import CheckoutForm from '../components/CheckoutForm';
import CheckoutPartnerForm from '../components/CheckoutPartnerForm';

import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Stack,
  Collapse,
  IconButton,
  CircularProgress,
  Divider,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';


export default function Tires() {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAdmin = user?.role === 'ADMIN';

  const [tires, setTires] = useState([]);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedSellerId, setSelectedSellerId] = useState('');
  const [partnerPrices, setPartnerPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterRef, setFilterRef] = useState('');
  const [tab, setTab] = useState(user?.role === 'ADMIN' ? 0 : 0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const fetchTires = async () => {
    setLoading(true);
    const res = await API.get('/tires');
    setTires(res.data);
    setLoading(false);
  };

  const fetchPartners = async () => {
    if (user?.role !== 'ADMIN') return;
    try {
      const res = await API.get('/partners');
      setPartners(res.data);
      if (res.data.length > 0) {
        setSelectedPartnerId((prev) => prev || String(res.data[0].id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    if (user?.role !== 'ADMIN') return;
    try {
      const res = await API.get('/users');
      setUsers(res.data || []);
      if (res.data.length > 0) {
        const selfId = user?.id ? String(user.id) : '';
        setSelectedSellerId((prev) => prev || selfId || String(res.data[0].id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPartnerPrices = async (partnerId) => {
    if (!partnerId) {
      setPartnerPrices({});
      return;
    }
    try {
      const res = await API.get(`/partners/${partnerId}/prices`);
      const map = {};
      res.data.forEach((row) => {
        map[String(row.tire_id)] = row.price;
      });
      setPartnerPrices(map);
    } catch (err) {
      console.error(err);
      setPartnerPrices({});
    }
  };

  useEffect(() => {
    fetchTires();
    fetchPartners();
    fetchUsers();
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      fetchPartnerPrices(selectedPartnerId);
    }
  }, [user?.role, selectedPartnerId]);

  const selectedPartner = partners.find(
    (p) => String(p.id) === String(selectedPartnerId)
  );

  const filteredTires = tires.filter((t) =>
    filterRef ? t.reference.toLowerCase().includes(filterRef.toLowerCase()) : true
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 0, sm: 3, md: 6 },
        py: { xs: 4, md: 5 },
        background: 'var(--app-bg)',
        overflowX: 'hidden',
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', md: 1200 },
          mx: 'auto',
          px: { xs: 0, sm: 0 },
        }}
      >
      {/* HERO */}
      <Box
        sx={{
          mb: { xs: 4, sm: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            lineHeight: 1.1,
            fontSize: { xs: 24, sm: 32, md: 42 },
          }}
        >
          Centre de controle des pneus
        </Typography>

        <Typography
          sx={{
            color: 'var(--text-muted)',
            mt: 1,
            maxWidth: 520,
            fontSize: { xs: 13, sm: 14 },
          }}
        >
          Gestion intelligente des pneus, du stock et des ventes - en temps reel.
        </Typography>

      </Box>

      {/* TABS */}
      <Paper
        sx={{
          mb: { xs: 3, sm: 5 },
          borderRadius: 4,
          background: 'var(--surface-3)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          indicatorColor="secondary"
          textColor="inherit"
          sx={{
            px: { xs: 1, sm: 2 },
            '& .MuiTabs-flexContainer': {
              justifyContent: { xs: 'flex-start', sm: 'center' },
            },
            '& .MuiTab-root': {
              color: 'var(--text-muted)',
              fontWeight: 700,
              minHeight: 44,
            },
            '& .Mui-selected': {
              color: 'var(--text-primary)',
            },
          }}
        >
          {user?.role === 'ADMIN' && <Tab label="Creer un pneu" />}
          <Tab label="Gerer les pneus" />
        </Tabs>
      </Paper>

      {/* TAB 1 - CREATE TIRE */}
      {user?.role === 'ADMIN' && tab === 0 && (
        <Box sx={{ px: { xs: 1.5, sm: 0 } }}>
          <Paper
            sx={{
              p: { xs: 2.5, sm: 4 },
              mb: { xs: 4, sm: 6 },
              borderRadius: 4,
              width: '100%',
              maxWidth: 900,
              mx: 'auto',
              boxSizing: 'border-box',
              background:
                'var(--surface-2)',
              backdropFilter: 'blur(12px)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 3 }}>
              Nouveau pneu
            </Typography>

            <TireForm
              onSuccess={() => {
                fetchTires();
                setTab(1);
              }}
            />
          </Paper>
        </Box>
      )}

      {/* TAB 2 - MANAGE TIRES */}
      {tab === (user?.role === 'ADMIN' ? 1 : 0) && (
        <>
          {/* FILTER */}
          <Box
            sx={{
              mb: { xs: 2.5, sm: 4 },
              width: '100%',
              p: { xs: 0, sm: 2 },
              borderRadius: { xs: 0, sm: 3 },
              background: { xs: 'transparent', sm: 'var(--surface-3)' },
              border: { xs: 'none', sm: '1px solid var(--border-subtle)' },
              backdropFilter: { xs: 'none', sm: 'blur(10px)' },
            }}
          >
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Autocomplete
                  freeSolo
                  options={tires.map((t) => t.reference)}
                  value={filterRef}
                  onInputChange={(e, v) => setFilterRef(v)}
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 420, md: 520 },
                    backgroundColor: 'var(--input-bg)',
                    borderRadius: 2,
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Rechercher par reference"
                      variant="filled"
                      size={isMobile ? 'small' : 'medium'}
                    />
                  )}
                />
                {isMobile && (
                  <IconButton
                    onClick={() => setFiltersOpen((v) => !v)}
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      backgroundColor: 'var(--surface-3)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <FilterListIcon sx={{ color: 'var(--text-primary)' }} />
                  </IconButton>
                )}
              </Stack>

              <Collapse in={!isMobile || filtersOpen} timeout="auto" unmountOnExit>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 1.5, sm: 2 }}
                  sx={{ width: '100%', flexWrap: { md: 'wrap' } }}
                >
                  {isAdmin && (
                    <TextField
                      select
                      label="Partenaire pour les prix"
                      value={selectedPartnerId}
                      onChange={(e) => setSelectedPartnerId(e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', md: 320 },
                        backgroundColor: 'var(--input-bg)',
                        borderRadius: 2,
                      }}
                      size={isMobile ? 'small' : 'medium'}
                    >
                      {partners.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name} {p.type === 'INTERN' ? '(Interne)' : ''}
                        </option>
                      ))}
                    </TextField>
                  )}
                  {isAdmin && (
                    <TextField
                      select
                      label="Vendeur"
                      value={selectedSellerId}
                      onChange={(e) => setSelectedSellerId(e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', md: 260 },
                        backgroundColor: 'var(--input-bg)',
                        borderRadius: 2,
                      }}
                      size={isMobile ? 'small' : 'medium'}
                    >
                      {users.map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {u.username}
                        </option>
                      ))}
                    </TextField>
                  )}
                </Stack>
              </Collapse>
            </Stack>
          </Box>

          {/* GRID */}
          {loading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ color: 'var(--text-primary)' }} />
            </Stack>
          ) : (
            <Grid
              container
              spacing={{ xs: 0, sm: 3 }}
              justifyContent="center"
              alignItems="stretch"
              sx={{ mx: 0, width: '100%', m: 0 }}
            >
              {filteredTires.map((tire) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={tire.tire_id}
                  sx={{ px: { xs: 0, sm: 0 } }}
                >
                  <Paper
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      p: { xs: 2.25, sm: 3 },
                      borderRadius: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      background:
                        'var(--surface-1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid var(--border-subtle)',
                      transition: '0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.35)',
                      },
                    }}
                    >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'var(--text-primary)',
                        fontWeight: 700,
                        textAlign: 'center',
                        fontSize: { xs: 16, sm: 18 },
                      }}
                    >
                      {tire.reference}
                    </Typography>

                    <Typography
                      sx={{
                        color: 'var(--text-muted)',
                        textAlign: 'center',
                        fontSize: { xs: 12, sm: 13 },
                      }}
                    >
                      {tire.manufacturer || '-'} {tire.supplier_name ? `- ${tire.supplier_name}` : ''}
                    </Typography>

                    <Divider sx={{ my: 2, opacity: 0.2, width: '80%' }} />

                    <Stack spacing={1} alignItems="center">
                      <Chip
                        label={`Stock : ${tire.stock}`}
                        color={tire.stock > 0 ? 'success' : 'error'}
                      />
                      {isAdmin && (
                        <Chip
                          label={`Prix Achat : ${tire.purchase_price ?? '-'} EUR`}
                          color="info"
                          variant="outlined"
                        />
                      )}
                      
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0}
                      mt={3}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        gap: { xs: 1, sm: 1.5 },
                        '& > *': {
                          flex: { xs: '1 1 0', sm: '0 0 auto' },
                          maxWidth: { xs: 'calc(50% - 4px)', sm: 'none' },
                        },
                      }}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CheckoutForm
                        tire={tire}
                        price={tire.sale_price}
                        onSuccess={fetchTires}
                        sellerId={isAdmin ? selectedSellerId : undefined}
                      />
                      {user?.role === 'ADMIN' && (
                        <CheckoutPartnerForm
                          tire={tire}
                          partnerId={selectedPartnerId}
                          partnerPrice={partnerPrices[String(tire.tire_id)]}
                          partner={selectedPartner}
                          onSuccess={fetchTires}
                          sellerId={selectedSellerId}
                        />
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
      </Box>
    </Box>
  );
}




